import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import SafeImage from '../SafeImage';
import api from '../../services/api';
import { ProgrammeIcon } from './ProgrammeUI';
import '../../styles/pages/ExperimentationZonePage.css';

const STATUS_OPTIONS = ['All Status', 'Active', 'Testing', 'Completed', 'Planned'];

const makeExcerpt = (description, maximumLength = 150) => {
  const normalized = String(description || '').replace(/\s+/g, ' ').trim();

  if (normalized.length <= maximumLength) return normalized;

  const shortened = normalized.slice(0, maximumLength);
  const finalSpace = shortened.lastIndexOf(' ');

  return `${shortened.slice(0, finalSpace > 90 ? finalSpace : maximumLength)}…`;
};

const ExperimentCardSkeleton = () => (
  <div aria-hidden="true" className="zone-experiment-card zone-experiment-card--skeleton">
    <span className="zone-experiment-card__image-skeleton" />
    <span className="zone-experiment-card__line zone-experiment-card__line--title" />
    <span className="zone-experiment-card__line" />
    <span className="zone-experiment-card__line zone-experiment-card__line--short" />
  </div>
);

const ExperimentDetailDialog = ({ experiment, onClose }) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!experiment) return undefined;

    const previouslyFocused = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements?.length) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [experiment, onClose]);

  if (!experiment) return null;

  return createPortal(
    <div
      className="zone-experiment-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section
        aria-describedby="zone-experiment-detail-description"
        aria-labelledby="zone-experiment-detail-title"
        aria-modal="true"
        className="zone-experiment-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <div className="zone-experiment-dialog__layout">
          <div className="zone-experiment-dialog__media">
            <SafeImage
              alt={experiment.title}
              height={760}
              placeholderText={experiment.title}
              src={experiment.image}
              width={680}
            />
            <span
              className="zone-experiment-card__badge"
              style={{ '--experiment-status': experiment.status_color }}
            >
              {experiment.status}
            </span>
          </div>

          <div className="zone-experiment-dialog__body">
            <div className="zone-experiment-dialog__header">
              <span>Featured experiment</span>
              <button
                aria-label={`Close ${experiment.title}`}
                className="zone-experiment-dialog__close"
                onClick={onClose}
                ref={closeButtonRef}
                type="button"
              >
                <ProgrammeIcon name="close" size={17} />
                Close
              </button>
            </div>
            <h2 id="zone-experiment-detail-title">{experiment.title}</h2>
            <p id="zone-experiment-detail-description">
              {experiment.description}
            </p>
            {experiment.metric ? (
              <div className="zone-experiment-dialog__metric">
                <span>Current update</span>
                <p>{experiment.metric}</p>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
};

const ExperimentationZoneProof = () => {
  const [experiments, setExperiments] = useState([]);
  const [filter, setFilter] = useState('All Status');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestVersion, setRequestVersion] = useState(0);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const closeExperiment = useCallback(() => {
    setSelectedExperiment(null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadExperiments = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.getExperiments(
          {},
          { signal: controller.signal },
        );

        if (!response.success || !Array.isArray(response.data)) {
          throw new Error('Unexpected experiments response');
        }

        setExperiments(response.data.filter((experiment) => experiment.active));
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('The featured experiments could not be loaded.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadExperiments();
    return () => controller.abort();
  }, [requestVersion]);

  const visibleExperiments = useMemo(() => {
    if (filter === 'All Status') return experiments;

    return experiments.filter((experiment) => experiment.status === filter);
  }, [experiments, filter]);

  return (
    <section
      aria-labelledby="featured-experiments-title"
      className="zone-featured-section"
    >
      <div className="pj-public-shell">
        <div className="zone-featured-heading">
          <div>
            <h2 id="featured-experiments-title">
              <span>Featured</span> Experiments
            </h2>
            <p aria-live="polite" role="status">
              {loading
                ? 'Loading experiments'
                : `${visibleExperiments.length} ${
                    visibleExperiments.length === 1
                      ? 'experiment'
                      : 'experiments'
                  } shown`}
            </p>
          </div>

          <div className="zone-status-filter">
            <label htmlFor="zone-experiment-status">Filter by status</label>
            <span className="zone-status-filter__control">
              <select
                id="zone-experiment-status"
                onChange={(event) => setFilter(event.target.value)}
                value={filter}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <ProgrammeIcon
                className="zone-status-filter__chevron"
                name="chevronDown"
                size={18}
              />
            </span>
          </div>
        </div>

        {loading ? (
          <div
            aria-busy="true"
            aria-label="Loading featured experiments"
            className="zone-experiment-grid"
          >
            {Array.from({ length: 3 }, (_, index) => (
              <ExperimentCardSkeleton key={index} />
            ))}
          </div>
        ) : error ? (
          <div className="zone-experiment-state" role="alert">
            <h3>Featured experiments are temporarily unavailable</h3>
            <p>{error}</p>
            <button
              onClick={() => setRequestVersion((version) => version + 1)}
              type="button"
            >
              Try again
            </button>
          </div>
        ) : visibleExperiments.length ? (
          <div className="zone-experiment-grid">
            {visibleExperiments.map((experiment) => (
              <article className="zone-experiment-card" key={experiment.id}>
                <div className="zone-experiment-card__media">
                  <SafeImage
                    alt={experiment.title}
                    height={420}
                    placeholderText={experiment.title}
                    src={experiment.image}
                    width={640}
                  />
                  <span
                    className="zone-experiment-card__badge"
                    style={{ '--experiment-status': experiment.status_color }}
                  >
                    {experiment.status}
                  </span>
                </div>

                <div className="zone-experiment-card__body">
                  <div className="zone-experiment-card__copy">
                    <h3 title={experiment.title}>{experiment.title}</h3>
                    <p>{makeExcerpt(experiment.description)}</p>
                  </div>
                  <div className="zone-experiment-card__footer">
                    {experiment.metric ? (
                      <p className="zone-experiment-card__metric">
                        {experiment.metric}
                      </p>
                    ) : (
                      <span aria-hidden="true" />
                    )}
                    <button
                      aria-label={`Read more about ${experiment.title}`}
                      className="zone-experiment-card__read-more"
                      onClick={() => setSelectedExperiment(experiment)}
                      type="button"
                    >
                      Read More
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="zone-experiment-state">
            <h3>No experiments match this status</h3>
            <p>Choose another status to explore the current programme.</p>
            <button onClick={() => setFilter('All Status')} type="button">
              Show all experiments
            </button>
          </div>
        )}
      </div>

      <ExperimentDetailDialog
        experiment={selectedExperiment}
        onClose={closeExperiment}
      />
    </section>
  );
};

export default ExperimentationZoneProof;
